import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  buildTenantRelativePath,
  clearStoredTenantPreviewSlug,
  getTenantPreviewSlugFromPath,
  getStoredTenantPreviewSlug,
  getTenantPreviewSlugFromSearch,
  isTenantPreviewHost,
  stripTenantPreviewPrefix,
  shouldCarryTenantPreview,
  storeTenantPreviewSlug,
} from "../utils/tenantPreview";
import { usePublicConfig } from "../hooks/usePublicConfig";

export default function TenantPreviewBoundary() {
  const location = useLocation();
  const navigate = useNavigate();
  // "/app" is the platform surface, mounted as a sibling route. An
  // unmatched /app/<x> URL (e.g. /app/admin) can fall through to the
  // tenant routes here, where react-router matches it as
  // :tenantSlug="app" because a dynamic+static branch (/:tenantSlug/admin)
  // outranks the /app/* catch-all. "app" is never a real tenant slug, so
  // bounce such URLs back to the platform before rendering any tenant UI.
  const isReservedAppPath =
    location.pathname.split("/").filter(Boolean)[0] === "app";
  const previewHost = isTenantPreviewHost();
  const shouldCarryPreview = shouldCarryTenantPreview(location.pathname);
  const { data: publicConfig } = usePublicConfig({
    enabled: previewHost && shouldCarryPreview,
  });

  useEffect(() => {
    if (isReservedAppPath) {
      return;
    }
    if (!previewHost) {
      clearStoredTenantPreviewSlug();
      return;
    }

    const slugFromPath = getTenantPreviewSlugFromPath(location.pathname);
    if (slugFromPath) {
      storeTenantPreviewSlug(slugFromPath);
    }

    const slugFromUrl = getTenantPreviewSlugFromSearch(location.search);
    if (slugFromUrl) {
      storeTenantPreviewSlug(slugFromUrl);
    }

    if (!shouldCarryPreview) {
      return;
    }

    const storedSlug = getStoredTenantPreviewSlug();
    if (!storedSlug) {
      return;
    }

    const fullCurrentPath = `${location.pathname}${location.search}${location.hash}`;
    const rebasedPath = `${stripTenantPreviewPrefix(
      location.pathname,
      slugFromPath
    )}${location.search}${location.hash}`;
    const repairedPath = buildTenantRelativePath(rebasedPath, storedSlug);

    if (repairedPath !== fullCurrentPath) {
      navigate(repairedPath, { replace: true });
    }
  }, [
    isReservedAppPath,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    previewHost,
    shouldCarryPreview,
  ]);

  useEffect(() => {
    if (isReservedAppPath || !previewHost || !shouldCarryPreview) {
      return;
    }

    const resolvedTenantSlug = `${publicConfig?.tenant?.slug || ""}`.trim();
    if (!resolvedTenantSlug) {
      return;
    }

    const currentPreviewSlug =
      getTenantPreviewSlugFromPath(location.pathname) ||
      getTenantPreviewSlugFromSearch(location.search) ||
      getStoredTenantPreviewSlug();
    if (!currentPreviewSlug || currentPreviewSlug === resolvedTenantSlug) {
      return;
    }

    storeTenantPreviewSlug(resolvedTenantSlug);
    const fullCurrentPath = `${location.pathname}${location.search}${location.hash}`;
    const rebasedPath = `${stripTenantPreviewPrefix(
      location.pathname,
      currentPreviewSlug
    )}${location.search}${location.hash}`;
    const repairedPath = buildTenantRelativePath(rebasedPath, resolvedTenantSlug);

    if (repairedPath !== fullCurrentPath) {
      navigate(repairedPath, { replace: true });
    }
  }, [
    isReservedAppPath,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    previewHost,
    publicConfig?.tenant?.slug,
    shouldCarryPreview,
  ]);

  if (isReservedAppPath) {
    return <Navigate to="/app" replace />;
  }

  return <Outlet />;
}
